import type { Map as MapboxMap } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type {
  ICapturedAadtExtracted,
  ICapturedAadtFeatureCollection,
  ILocation,
} from '@/types/api';
import { GEOSPATIAL_LAYER_LIST, LayerId } from '../constants';
import { useLocationGeospatialQuery } from '../services/locations.api';
import { extractCapturedAadtData } from '../utils/extractCapturedAadtData';
import { MapLayersService } from '../utils/MapLayersService';
import { metersToKilometers } from '../utils/radiusHelpers';
import { useHoverCircleOnLine } from './useHoverCircleOnLine';
import { useMapInteractions } from './useMapInteractions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseLocationDetailLayersOptions {
  /** Mapbox GL map ref shared by the enclosing MapShell. */
  map: RefObject<MapboxMap | null>;
  /** Whether the Mapbox style has finished loading. */
  isMapLoaded: boolean;
  /** Whether the custom icon sprites have been initialised. */
  imagesInitialized: boolean;
  /** The location whose per-location detail layers should be rendered,
   *  or `undefined` when no location is selected. */
  currentLocation: ILocation | undefined;
}

export interface UseLocationDetailLayersResult {
  /**
   * The `MapLayersService` instance created for the current location, or
   * `undefined` when the guard conditions are not yet met (map not loaded,
   * no location selected, or geospatial data still fetching).
   *
   * Consumers (e.g. MarkerMetrics) need this reference to drive layer
   * visibility filters and route overlays.
   */
  layersService: MapLayersService | undefined;
  /**
   * The extracted captured-AADT data (value + route properties), or
   * `undefined` while loading.  Derived from the captured-AADT geospatial
   * query and pre-computed so the caller does not have to repeat the
   * extraction.
   */
  capturedAadtData: ICapturedAadtExtracted | undefined;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * `useLocationDetailLayers` — shared per-location geospatial layer effect.
 *
 * Orchestrates everything needed to render the detail layers for a single
 * selected location on a Mapbox map:
 *   - dashed radius circle
 *   - max-AADT highlight + interactions
 *   - hover-cursor circles on the TRAFFIC_MAX_AADT and MAX_AADT_CIRCLE layers
 *   - captured-AADT layer
 *   - layer z-ordering via `orderMiddleLayers`
 *
 * The hook is **side-effect only** for layers: it creates a `MapLayersService`
 * for the current location, adds the layers in the canonical order, wires up
 * interactions, and tears them all down on unmount or when the selected
 * location changes.  It does NOT wire clusters, cluster interactions, or the
 * manual-single-location-select logic — those belong to the consumer.
 *
 * @param options - See {@link UseLocationDetailLayersOptions}.
 * @returns `layersService` (for callers that need to drive the service after
 *   mount, e.g. `MarkerMetrics`) and `capturedAadtData` (pre-extracted from
 *   the captured-AADT geospatial query).
 */
export function useLocationDetailLayers(
  options: UseLocationDetailLayersOptions,
): UseLocationDetailLayersResult {
  const { map, isMapLoaded, imagesInitialized, currentLocation } = options;

  // ── Geospatial queries (two layers, lazy by location id) ───────────────────
  const queryEnabled = !!currentLocation?.id;

  const { data: geospatialMaxAadtSegment } = useLocationGeospatialQuery(
    currentLocation?.id,
    GEOSPATIAL_LAYER_LIST.MAX_AADT,
    { enabled: queryEnabled },
  );

  const { data: geospatialCapturedAadtSegment } = useLocationGeospatialQuery(
    currentLocation?.id,
    GEOSPATIAL_LAYER_LIST.CAPTURED_AADT,
    { enabled: queryEnabled },
  );

  // ── Extracted captured-AADT data (memoised, exposed to caller) ────────────
  const capturedAadtData = useMemo<ICapturedAadtExtracted | undefined>(() => {
    if (!geospatialCapturedAadtSegment) return undefined;
    return extractCapturedAadtData(geospatialCapturedAadtSegment as ICapturedAadtFeatureCollection);
  }, [geospatialCapturedAadtSegment]);

  // ── Interaction hooks (created unconditionally per React rules-of-hooks) ───
  const { setupInteractions } = useMapInteractions(map);
  // MAX_AADT_CIRCLE hover cursor
  const { setupHoverTrafficCursor: setupMaxAadtCircle } = useHoverCircleOnLine({
    lineLayerId: LayerId.MAX_AADT_CIRCLE,
    map,
  });
  // Max-AADT segments hover cursor
  const { setupHoverTrafficCursor } = useHoverCircleOnLine({
    lineLayerId: LayerId.TRAFFIC_MAX_AADT,
    map,
  });

  // ── Layer service state (exposed to the caller after the effect fires) ──────
  const [layersService, setLayersService] = useState<MapLayersService | undefined>(undefined);

  // Tracks whether the guards-passed branch below has ever run, so a
  // deselect only tears down layers that actually exist — a mount with no
  // location selected must not construct a MapLayersService at all.
  const hasAddedLayersRef = useRef(false);

  // ── Main per-location layers effect ───────────────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: specific useEffect condition
  useEffect(() => {
    const cleanupFns: Array<() => void> = [];

    // Primary guard: map fully initialized before wiring layers
    if (!map.current || !isMapLoaded || !imagesInitialized) return;

    if (!currentLocation) {
      // Deselected — remove the previous location's layers, if any.
      if (hasAddedLayersRef.current) {
        new MapLayersService(map.current).removeLocationDetailLayers();
        hasAddedLayersRef.current = false;
      }
      setLayersService(undefined);
      return;
    }

    // No `currentLocationValues` guard needed: this hook only renders geospatial
    // layers and takes a Location directly. The metrics-panel render gate keeps
    // guarding on `currentLocationValues` independently.
    if (!geospatialMaxAadtSegment || !geospatialCapturedAadtSegment) {
      // Switching to a new location whose data hasn't arrived yet — keep the
      // previous location's layers on screen instead of flashing empty.
      return () => {
        cleanupFns.forEach((fn) => fn());
      };
    }

    // All guards passed — build the layer service and add layers in canonical order

    const newLayersService = new MapLayersService(map.current);
    setLayersService(newLayersService);
    hasAddedLayersRef.current = true;

    const locationLngLat: [number, number] = [
      currentLocation.coordinates.longitude,
      currentLocation.coordinates.latitude,
    ];

    newLayersService.addDashedCircle(locationLngLat, metersToKilometers(currentLocation.radius));
    newLayersService.addMaxAadtLayer(geospatialMaxAadtSegment);

    // Max-AADT interactions (hover tooltip + feature-state)
    cleanupFns.push(setupInteractions(), setupMaxAadtCircle(), setupHoverTrafficCursor());

    const capturedAadtRaw = geospatialCapturedAadtSegment as ICapturedAadtFeatureCollection;
    newLayersService.addCapturedAadtLayer(capturedAadtRaw);

    newLayersService.orderMiddleLayers();

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [
    currentLocation,
    isMapLoaded,
    imagesInitialized,
    geospatialMaxAadtSegment,
    geospatialCapturedAadtSegment,
  ]);

  // ── Unmount teardown ─────────────────────────────────────────────────────
  // The main effect's cleanup only tears down interaction listeners: on a
  // location-to-location transition, `MapLayersService.add*` updates
  // existing sources in place rather than removing them, so removing the
  // layers there would flicker. That leaves the Mapbox layers themselves
  // orphaned when the consumer unmounts outright (e.g. its owning workspace
  // gets deleted) — this effect's cleanup runs exactly once, on unmount, to
  // remove them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally unmount-only
  useEffect(() => {
    return () => {
      if (hasAddedLayersRef.current && map.current) {
        new MapLayersService(map.current).removeLocationDetailLayers();
        hasAddedLayersRef.current = false;
      }
    };
  }, []);

  return { layersService, capturedAadtData };
}
